import { Vector3 } from 'three';

import { isCloseVectors, isClose } from '../geom-utils';
import { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody } from '../loaders/body';
import type { RendererState } from './renderer';

/*
This will collect translation vectors for every affected body in the scene in an absolute manner.
We will not apply translations to assembly instance groups so we do not have to dynamically recalculate
bounding box centers. We will recursively collect global transformation information for each body in
the scene based on the prescribed explosion state.
*/
export const collectExplodedStateTranslationInfo = (
  thisState: RendererState,
  nextExplodedState: Record<string, number>,
): Record<string, Vector3> => {
  const { bodyOrAssemblyInstanceById } = thisState;

  // first collect where every body should be relative to itself given the current explode state
  const translationInfo: Record<string, Vector3> = {};
  for (const assemblyInstanceKey in nextExplodedState) {
    const assemblyInstance =
      bodyOrAssemblyInstanceById.get(assemblyInstanceKey);
    //`assemblyInstance` should be an assembly instance. But sometimes there is a bug where the `explodeState`
    //incorrectly has keys that are not assemblyInstances. This tends to happen if the `ExplodedViewSlider` is not
    //closed after the `focusedBodies[0]` (the active viewerId) changes to a body.
    //This bug is fixed in PP-15834, and shouldn't happen again. But as defence against this bug, check if it is really an AssemblyInstance.
    if (
      !(assemblyInstance instanceof AssemblyInstance) ||
      !assemblyInstance?.isLoaded
    )
      continue;
    for (const child of assemblyInstance?.children() ?? []) {
      const explodeVector = child.getExplodeTranslationVector(
        assemblyInstance.initialBoxMidpoint,
        nextExplodedState[assemblyInstanceKey],
      );
      if (child instanceof AssemblyInstance) {
        for (const subChild of child.descendentBodies()) {
          const id = subChild.id as string;
          if (translationInfo[id]) {
            translationInfo[id].add(explodeVector);
          } else {
            translationInfo[id] = explodeVector.clone();
          }
        }
      } else {
        const id = child.id as string;
        if (translationInfo[id]) {
          translationInfo[id].add(explodeVector);
        } else {
          translationInfo[id] = explodeVector.clone();
        }
      }
    }
  }

  // now given where the bodies should be and where they currently are, extract a translation vector
  // to perform on each individual body
  const resolvedTranslationInfo: Record<string, Vector3> = {};
  for (const bodyId in translationInfo) {
    const body = bodyOrAssemblyInstanceById.get(bodyId) as
      | AnyBody
      | AssemblyInstance;
    if (!body?.isLoaded) continue;
    const endPosition = new Vector3().addVectors(
      translationInfo[bodyId],
      body.initialPosition,
    );
    if (!isCloseVectors(endPosition, body.group.position)) {
      resolvedTranslationInfo[body.id as string] = new Vector3().subVectors(
        endPosition,
        body.group.position,
      );
    }
  }

  return resolvedTranslationInfo;
};

export const resetExplode = (thisState: RendererState): boolean => {
  let shouldUpdate = false;
  const fakedExplodedState: Record<string, number> = {};
  for (const key in thisState.explodedState) {
    if (!isClose(thisState.explodedState[key], 0)) {
      shouldUpdate = true;
      fakedExplodedState[key] = 0.0;
    }
  }

  if (shouldUpdate) {
    const { bodyOrAssemblyInstanceById } = thisState;
    const resolvedTranslationInfo = collectExplodedStateTranslationInfo(
      thisState,
      fakedExplodedState,
    );
    for (const bodyId in resolvedTranslationInfo) {
      const body = bodyOrAssemblyInstanceById.get(bodyId) as AnyBody;
      body.translate(resolvedTranslationInfo[bodyId]);
    }
  }

  return shouldUpdate;
};

export const handleUpdateExplodedState = (
  thisState: RendererState,
  nextState: RendererState,
): boolean => {
  let shouldUpdate = false;
  for (const nextKey in nextState.explodedState) {
    const thisValue = thisState.explodedState[nextKey];
    const nextValue = nextState.explodedState[nextKey];
    if (thisValue !== nextValue) {
      shouldUpdate = true;
      break;
    }
  }

  if (!shouldUpdate) {
    // this means we are calling for an explicit reset of explosion state
    if (
      //not empty
      Object.keys(
        typeof thisState.explodedState === 'object'
          ? thisState.explodedState
          : {},
      ).length &&
      //is empty
      !Object.keys(
        typeof nextState.explodedState === 'object'
          ? nextState.explodedState
          : {},
      ).length
    ) {
      shouldUpdate = resetExplode(thisState);
    }
  } else {
    const { bodyOrAssemblyInstanceById } = thisState;
    const resolvedTranslationInfo = collectExplodedStateTranslationInfo(
      thisState,
      nextState.explodedState,
    );
    for (const bodyId in resolvedTranslationInfo) {
      const body = bodyOrAssemblyInstanceById.get(bodyId) as AnyBody;
      body.translate(resolvedTranslationInfo[bodyId]);
    }
  }

  return shouldUpdate;
};
